import "./DownspoutModal.css";
import closeButton from "../../assets/icons/close.svg";
import { useState, useEffect } from "react";

export default function DownspoutModal({
  setActiveModal,
  activeModal,
  setTool,
  setIsDownspoutModalOpen,
  addDownspout,
  mode = "create", // "create" | "edit"
  initialData = null, // downspout line object when editing
  onClose, // optional callback
}) {
  const [elbowSequence, setElbowSequence] = useState("");
  const [totalFootage, setTotalFootage] = useState("");
  const [splashBlock, setSplashBlock] = useState(false);
  const [rainBarrel, setRainBarrel] = useState(false);
  const [undergroundDrainage, setUndergroundDrainage] = useState(false);
  const [downspoutSize, setDownspoutSize] = useState("2x3 corrugated");
  const [downspoutMaterial, setDownspoutMaterial] = useState("aluminum");
  const [footageIsValid, setFootageIsValid] = useState(null);
  const [elbowSequenceIsValid, setElbowSequenceIsValid] = useState(true);
  const [profile, setProfile] = useState("");

  useEffect(() => {
    if (activeModal !== "downspout") return;
    if (mode === "edit" && initialData) {
      setElbowSequence(String(initialData.elbowSequence || "").toUpperCase());
      setTotalFootage(String(initialData.measurement ?? ""));
      setSplashBlock(!!initialData.splashBlock);
      setRainBarrel(!!initialData.rainBarrel);
      setUndergroundDrainage(!!initialData.undergroundDrainage);
      // Try to reconstruct size label from stored ds
      setDownspoutSize(initialData.downspoutSize || "2x3 corrugated");
      setDownspoutMaterial("aluminum"); // your UI hides this anyway
      setFootageIsValid(true);
      setElbowSequenceIsValid(true);
    } else {
      clearInputs();
    }
  }, [activeModal, mode, initialData]);

  function handleDownspoutSubmit(e) {
    e.preventDefault();

    const downspoutData = {
      elbowSequence,
      totalFootage,
      splashBlock,
      rainBarrel,
      undergroundDrainage,
      downspoutSize,
      downspoutMaterial,
      profile:
        downspoutSize.split(" ").length > 1
          ? downspoutSize.split(" ")[1]
          : downspoutSize.split(" ")[0],
    };

    // This updates/creates the DS in lines (your existing wiring)
    addDownspout(downspoutData);

    // Close the DS modal
    setIsDownspoutModalOpen(false);

    // Do NOT open confirm here; just go back to the diagram modal.
    setActiveModal("diagram");

    // Leave tool as-is (optional; preserves select state)
    setTool?.((prev) => prev);

    onClose?.();
  }

  function handleChangeElbowSequence(e) {
    const v = (e.target.value || "").toUpperCase();
    validateElbowSequence(v);
    setElbowSequence(v);
  }

  function handleChangeTotalFootage(e) {
    validateFootage(e.target.value);
    setTotalFootage(e.target.value);
  }

  function handleChangeDownspoutSize(e) {
    setDownspoutSize(e.target.value);
  }

  function handleChangeDownspoutMaterial(e) {
    setDownspoutMaterial(e.target.value);
  }

  function clearInputs() {
    setElbowSequence("");
    setTotalFootage("");
    setSplashBlock(false);
    setRainBarrel(false);
    setUndergroundDrainage(false);
    setDownspoutSize("2x3 corrugated");
    setDownspoutMaterial("aluminum");
  }

  function disableCreateButton() {
    if (
      elbowSequence === "" ||
      totalFootage === "" ||
      totalFootage[0] === "0"
    ) {
      return true;
    }

    if (!footageIsValid || !elbowSequenceIsValid) {
      return true;
    }

    return false;
  }

  function validateFootage(input) {
    const pattern = /^[0-9]*$/;
    setFootageIsValid(pattern.test(input));
    return pattern.test(input);
  }

  function validateElbowSequence(input) {
    const pattern = /^[A, a, B, b, C, c, 2, 4, 6]*$/;
    setElbowSequenceIsValid(pattern.test(input));
  }

  return (
    <div
      className={
        activeModal === "downspout"
          ? `modal downspout-modal modal_visible`
          : "modal downspout-modal"
      }
    >
      <form className="downspout-modal__form" onSubmit={handleDownspoutSubmit}>
        <header>
          <h2 style={{ margin: "15px 0" }}>
            {mode === "edit" ? "Update Downspout" : "Downspout Details"}
          </h2>
        </header>
        <div className="downspout-modal__body">
          <label className="add-item__label">
            Elbow Sequence:{" "}
            <span
              className={
                elbowSequenceIsValid === true
                  ? "elbow-sequence__input_visible"
                  : "elbow-sequence__input"
              }
            >
              Use: A, B, 2, 4, and 6 for elbows and offsets.
            </span>
            <span
              className={
                elbowSequenceIsValid === true || elbowSequenceIsValid === null
                  ? "elbow-sequence__input-error"
                  : "elbow-sequence__input-error_visible"
              }
            >
              Only accepts: A, B, C, 2, 4, and 6
            </span>
            <input
              className={
                elbowSequenceIsValid === false
                  ? "add-item-name__input add-item-form__input add-item-form__input_error"
                  : "add-item-name__input add-item-form__input"
              }
              type="text"
              onChange={handleChangeElbowSequence}
              value={elbowSequence}
              placeholder="Ex: AA62A, AAA, BB4A"
            />
          </label>
          <label className="add-item__label">
            Total Footage:{" "}
            <span
              className={
                footageIsValid === false
                  ? "footage-label-error_visible"
                  : "footage-label-error"
              }
            >
              Only input numbers.
            </span>
            <input
              className={
                footageIsValid === false
                  ? "add-item-name__input add-item-form__input add-item-form__input_error"
                  : "add-item-name__input add-item-form__input"
              }
              type="text"
              onChange={handleChangeTotalFootage}
              value={totalFootage}
            />
          </label>
          <label className="downspout__drop-label">
            Downspout Size
            <select
              className="downspout__drop-input"
              onChange={handleChangeDownspoutSize}
              value={downspoutSize}
            >
              <option className="input__drop" value="2x3 corrugated">
                2x3 Corrugated
              </option>
              <option className="input__drop" value="3x4 corrugated">
                3x4 Corrugated
              </option>
              <option className="input__drop" value="3x4 smooth">
                3x4 Smooth
              </option>
              <option className="input__drop" value="4x5 corrugated">
                4x5 Corrugated
              </option>
              <option className="input__drop" value='3" Round'>
                3" Round
              </option>
              <option className="input__drop" value='4" Round'>
                4" Round
              </option>
              <option className="input__drop" value="Box">
                Box
              </option>
            </select>
          </label>
          <label style={{ display: "none" }} className="downspout__drop-label">
            Downspout Material
            <select
              className="downspout__drop-input"
              onChange={handleChangeDownspoutMaterial}
              value={downspoutMaterial}
            >
              <option className="input__drop" value="aluminum">
                Aluminum
              </option>
              <option className="input__drop" value="galvalume">
                Galvalume
              </option>
              <option className="input__drop" value="copper">
                Copper
              </option>
            </select>
          </label>
        </div>
        <div className="add-item-form__footer">
          <button
            onClick={() => {
              setIsDownspoutModalOpen(false);
              if (mode === "create") {
                setTool("downspout");
              } else {
                // stay in Select on edit
                setTool?.((prev) => prev); // no-op if provided
              }
              setActiveModal("diagram");
              onClose?.();
            }}
            type="button"
            className="add-item-form__button_cancel"
          >
            Cancel
          </button>

          <button
            type="submit"
            className="add-item-form__button_create"
            disabled={disableCreateButton()}
          >
            {mode === "edit" ? "Update" : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
}
